import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "../styles/NavBar.css";
import Logo from "../images/Logo.svg";
import { canManageAccounts, PERMISSIONS } from "../utils/rbac";
import {
  deniedActionClass,
  usePermissionGuard,
} from "../hooks/usePermissionGuard";
import {
  DashboardIcon,
  ManageEmployeeIcon,
  ManageAccountsIcon,
  PayslipIcon,
  PayslipListIcon,
  InvoicesIcon,
  PayslipMetaDataIcon,
} from "./NavIcons";

const NavBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { guardAction, PermissionDialog, isAllowed } = usePermissionGuard();
  const canGeneratePayslip = isAllowed(PERMISSIONS.CREATE_PAYSLIP);
  const [, setSessionTick] = useState(0);
  const path = location.pathname;

  const isDashboard = path === "/dashboard";
  const isManageEmployee =
    path === "/manageEmployee" ||
    path === "/userProfile" ||
    path === "/addEmployee" ||
    path === "/editEmployee" ||
    path === "/newEmployeeYtd";
  const isManageAccounts =
    path === "/manageAccounts" ||
    path === "/manageAccounts/add" ||
    path.startsWith("/manageAccounts/edit/") ||
    path === "/myAccount";
  const isEmployeeYtd = path === "/employeeYtd";
  const isPayslip = path === "/generateInvoice";
  const isPayslipList = path === "/invoiceList" || path === "/invoiceDetail";
  const isInvoicesSection =
    path === "/invoices" ||
    path === "/generateInvoiceNew" ||
    path === "/previewInvoice";
  const isPayslipMeta = path === "/payslipmetadata";
  const isAdminAccounts = canManageAccounts();
  const accountsNavPath = isAdminAccounts ? "/manageAccounts" : "/myAccount";
  const accountsNavLabel = isAdminAccounts ? "Manage Accounts" : "My Account";

  useEffect(() => {
    const onSessionUpdated = () => setSessionTick((n) => n + 1);
    window.addEventListener("user-session-updated", onSessionUpdated);
    return () =>
      window.removeEventListener("user-session-updated", onSessionUpdated);
  }, []);

  const navLinkClass = (active) => `nav-link${active ? " active-link" : ""}`;

  return (
    <nav className="navbar-container">
      <PermissionDialog />
      <div className="greeting">
        <p className="link-text">
          <img src={Logo} alt="Logo" />
          Surplus Payroll
        </p>
      </div>

      <ul className="nav-links space-y-2">
        <li className="nav-item">
          <Link to="/dashboard" className={navLinkClass(isDashboard)}>
            <span className="nav-icon-slot">
              <DashboardIcon active={isDashboard} />
            </span>
            <span className="link-text">Dashboard</span>
          </Link>
        </li>

        <li className="nav-item">
          <Link to="/manageEmployee" className={navLinkClass(isManageEmployee)}>
            <span className="nav-icon-slot">
              <ManageEmployeeIcon active={isManageEmployee} />
            </span>
            <span className="link-text">Manage Employee</span>
          </Link>
        </li>

        <li className="nav-item">
          <Link
            to={accountsNavPath}
            className={navLinkClass(isManageAccounts)}
          >
            <span className="nav-icon-slot">
              <ManageAccountsIcon active={isManageAccounts} />
            </span>
            <span className="link-text">{accountsNavLabel}</span>
          </Link>
        </li>

        <li className="nav-item">
          <Link to="/employeeYtd" className={navLinkClass(isEmployeeYtd)}>
            <span className="nav-icon-slot nav-icon-slot--dollar">
              <span className="nav-side-dollar-icon" aria-hidden="true">
                $
              </span>
            </span>
            <span className="link-text">Manage YTD</span>
          </Link>
        </li>

        <li className="nav-item">
          <Link
            to="/generateInvoice"
            className={`${navLinkClass(isPayslip)} ${deniedActionClass(canGeneratePayslip)}`}
            onClick={(e) => {
              e.preventDefault();
              guardAction(
                PERMISSIONS.CREATE_PAYSLIP,
                "generate payslip",
                () => {
                  navigate("/generateInvoice");
                },
              );
            }}
          >
            <span className="nav-icon-slot">
              <PayslipIcon active={isPayslip} />
            </span>
            <span className="link-text">Payslip</span>
          </Link>
        </li>

        <li className="nav-item">
          <Link to="/invoiceList" className={navLinkClass(isPayslipList)}>
            <span className="nav-icon-slot">
              <PayslipListIcon active={isPayslipList} />
            </span>
            <span className="link-text">Payslip List</span>
          </Link>
        </li>

        <li className="nav-item">
          <Link to="/invoices" className={navLinkClass(isInvoicesSection)}>
            <span className="nav-icon-slot">
              <InvoicesIcon active={isInvoicesSection} />
            </span>
            <span className="link-text">Invoices</span>
          </Link>
        </li>

        <li className="nav-item">
          <Link to="/payslipmetadata" className={navLinkClass(isPayslipMeta)}>
            <span className="nav-icon-slot">
              <PayslipMetaDataIcon active={isPayslipMeta} />
            </span>
            <span className="link-text">Payslip Meta Data</span>
          </Link>
        </li>
      </ul>
    </nav>
  );
};

export default NavBar;
